/** spec/admin-ui/navigation.md: admin-ui-navigation-R18–R20, R24–R27, R32, R54, R88. */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { SessionInfo } from '@/types/auth'
import type { VersionInfo } from '@/types'
import {
  AuthRequestError, beginLoginEffect, currentPanelAddress, getSessionEffect,
  getVersionEffect, logoutEffect, navigateEffect, removeLegacyAuthEffect,
} from './effects/auth.effects'

const signInFailures = {
  denied: 'Sign-in was denied.',
  expired: 'Sign-in expired. Please try again.',
  failed: 'Sign-in failed. Please try later.',
} as const
export const useAuthStore = defineStore('auth', () => {
  const session = ref<SessionInfo | null>(null)
  const initialized = ref(false)
  const isLoading = ref(true)
  const sessionEnded = ref(false)
  const notice = ref<string | null>(null)
  const version = ref<VersionInfo | null>(null)
  const canSignIn = ref(false)
  let initialization: Promise<void> | null = null
  let signingOut: Promise<boolean> | null = null
  let failedSignIn = false
  const isAuthenticated = computed(() => session.value !== null && !sessionEnded.value)
  // Keep the loaded workspace and its forms mounted after session expiry (R24, R54).
  const hasWorkspace = computed(() => session.value !== null)
  const currentUsername = computed(() => session.value?.login ?? null)
  const userFinalPrivileges = computed(() => session.value?.privileges ?? [])

  function can(privilege: string): boolean { return userFinalPrivileges.value.includes(privilege) }
  function initialize(address: string): Promise<void> {
    initialization ??= loadSession(address)
    return initialization
  }
  async function loadSession(address: string): Promise<void> {
    removeLegacyAuthEffect()
    const url = new URL(address, window.location.origin)
    failedSignIn = url.pathname === '/' && url.searchParams.has('sso_error')
    if (failedSignIn) {
      const reason = url.searchParams.get('sso_error') ?? ''
      notice.value = Object.hasOwn(signInFailures, reason)
        ? signInFailures[reason as keyof typeof signInFailures] : signInFailures.failed
    }
    void getVersionEffect().then((value): void => { version.value = value }).catch((): void => {})
    try {
      session.value = await getSessionEffect()
    } catch (error) {
      if (error instanceof AuthRequestError && error.code === 'unauthenticated') {
        if (failedSignIn) canSignIn.value = true
        else beginLoginEffect(address)
      } else {
        notice.value = error instanceof AuthRequestError ? error.message
          : 'The server rejected the request without a reason.'
      }
    } finally {
      initialized.value = true
      isLoading.value = false
    }
  }
  function endSession(): void {
    if (!hasWorkspace.value) return
    sessionEnded.value = true
    canSignIn.value = true
    notice.value = 'Your session has ended. Please sign in again.'
  }
  function login(): void {
    beginLoginEffect(!hasWorkspace.value && failedSignIn ? '/' : currentPanelAddress())
  }
  function logout(): Promise<boolean> {
    signingOut ??= performLogout().finally((): void => { signingOut = null })
    return signingOut
  }
  async function performLogout(): Promise<boolean> {
    const endpoint = session.value?.end_session_url
    if (!endpoint) return false
    try {
      try { await logoutEffect() } catch (error) {
        if (!(error instanceof AuthRequestError) || error.code !== 'unauthenticated') throw error
      }
      session.value = null
      sessionEnded.value = false
      canSignIn.value = false
      notice.value = null
      navigateEffect(endpoint)
      return true
    } catch (error) {
      notice.value = error instanceof AuthRequestError ? error.message
        : 'No response was received from the server.'
      return false
    }
  }
  return { initialized, isLoading, isAuthenticated, hasWorkspace, sessionEnded, canSignIn,
    notice, currentUsername, userFinalPrivileges, version, can, initialize, endSession, login, logout }
})
export type AuthStore = ReturnType<typeof useAuthStore>
